import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type DependentProfileVersion } from '@/features/tax-profile/individual-profiles/DependentProfile'
import { type IndividualProfileStore } from '@/features/tax-profile/individual-profiles/IndividualProfileStore'
import { type SnackbarErrorHandlerFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'

/**
 * View model for the delete dependent dialog.
 */
export class DeleteDependentDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  @observable accessor dependent: DependentProfileVersion | null = null

  constructor(
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _individualProfileStore: IndividualProfileStore,
  ) {}

  /**
   * Prompts for and performs deletion if accepted.
   *
   * @param dependent
   * @param signal
   * @returns A boolean promise that resolves to true if the
   *  dependent was deleted, or false if the operation was cancelled or
   *  the user declined.
   */
  async prompt(dependent: DependentProfileVersion, signal?: AbortSignal) {
    // Close any ongoing prompt
    this.modal.close()

    // Start a new prompt
    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.dependent = dependent
      this.modal.show()
    })

    // Wait for the user to confirm or cancel
    const response = await responsePromise

    // Close the dialog
    this.modal.close()

    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  delete = task.resolved(async () => {
    if (!this.dependent) {
      return
    }

    await this._individualProfileStore
      .deleteDependentProfile(this.dependent.id)
      .catch(this._createSnackbarErrorHandler('Deleting the dependent failed'))

    this._prompt.respond(true)
    this.modal.close()
  })
}
